import {
  ColumnDef,
  ColumnPinningState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  Column,
} from "@tanstack/react-table";
import { Fragment, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { DEFAULT_ITEMS_PER_PAGE } from "../shared/constants";
import Pagination from "./Pagination";
import { cn } from "../lib/utils";

interface SummaryKeyConfig<T = unknown> {
  key: string;
  value?: string | string[];
  customValue?: string;
  customCalculation?: (data: T[]) => string | number;
  align?: string;
  prepend?: string;
  append?: string;
  precision?: number;
  className?: string;
}

interface TableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  isLoading?: boolean;
  loadingRowCount?: number;
  showSearch?: boolean;
  searchPosition?: "end" | "start" | "center";
  onSearch?: (value: string) => void;
  className?: string;
  prependWithSearch?: ReactNode;
  appendWithSearch?: ReactNode;
  title?: string | ReactNode;
  pageSize?: number;
  maxHeight?: string;
  loadingHeight?: number;
  summaryKeys?: SummaryKeyConfig<T>[];
  summaryPosition?: "top" | "bottom";
  sortByKey?: string;
  sortOrder?: "asc" | "desc";
  showPagination?: boolean;
  isMobile?: boolean;
  leftPinnedColumnIds?: string[];
  rightPinnedColumnIds?: string[];
  renderRowDetails?: (row: T) => ReactNode;
  onRowClick?: (row: T) => void;
  onSort?: (sorting: SortingState) => void;
  manualPagination?: boolean;
  manualSorting?: boolean;
  manualFiltering?: boolean;
  pageCount?: number;
  totalItemsCount?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  initialExpandedRows?: Record<string, boolean>;
}

// Helper to get nested value by dot notation
function getNestedValue(obj: unknown, path: string): unknown {
  if (typeof obj !== "object" || obj === null) return undefined;
  return path.split(".").reduce((acc: unknown, part: string) => {
    if (typeof acc === "object" && acc !== null && part in acc) {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, obj);
}

// Helper for sticky pinning styles (TanStack Table v8)
function getCommonPinningStyles<T>(
  column: Column<T>,
  isScrolledLeft: boolean,
  isScrolledRight: boolean,
  stickPosition: "bottom" | "top" | "both" = "bottom"
): React.CSSProperties {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn =
    isPinned === "left" && column.getIsLastColumn("left");
  const isFirstRightPinnedColumn =
    isPinned === "right" && column.getIsFirstColumn("right");

  const showLeftShadow = isLastLeftPinnedColumn && isScrolledLeft;
  const showRightShadow = isFirstRightPinnedColumn && isScrolledRight;

  const shadows: string[] = [];

  // 👇 Bottom border shadow (equivalent to Tailwind's shadow-[inset_0_-1px_0_0_#e5e5e5])
  if (stickPosition === "both") {
    shadows.push(`inset 0 -1px 0 0 #e5e5e5`);
    shadows.push(`inset 0 1px 0 0 #e5e5e5`);
  } else {
    shadows.push(
      `inset 0 ${stickPosition === "bottom" ? "-1px" : "1px"} 0 0 #e5e5e5`
    );
  }

  // 👇 Left/right scroll shadows
  if (showLeftShadow) {
    shadows.push("inset -4px 0 4px -4px gray");
  } else if (showRightShadow) {
    shadows.push("inset 4px 0 4px -4px gray");
  }

  return {
    boxShadow: shadows.join(", "),
    left: isPinned === "left" ? `${column.getStart("left") ?? 0}px` : undefined,
    right:
      isPinned === "right" ? `${column.getAfter("right") ?? 0}px` : undefined,
    position: isPinned ? "sticky" : "unset",
    width: column.getSize(),
    zIndex: isPinned ? 1 : 0,
    background: "#fff", // to ensure shadows appear over table background
  };
}

export function Table<T>({
  data,
  columns,
  isLoading = false,
  loadingRowCount = 3,
  showSearch = false,
  searchPosition = "end",
  onSearch,
  className = "",
  prependWithSearch = null,
  appendWithSearch = null,
  pageSize = DEFAULT_ITEMS_PER_PAGE,
  title = null,
  maxHeight = "500px",
  loadingHeight = 3,
  summaryKeys,
  summaryPosition = "bottom",
  sortByKey,
  sortOrder,
  showPagination = true,
  leftPinnedColumnIds = [],
  rightPinnedColumnIds = [],
  renderRowDetails,
  onRowClick,
  onSort,
  manualPagination = false,
  manualSorting = false,
  manualFiltering = false,
  pageCount: propPageCount,
  totalItemsCount,
  onPageChange: propOnPageChange,
  onPageSizeChange: propOnPageSizeChange,
  initialExpandedRows = {},
}: TableProps<T>) {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>(() =>
    sortByKey && sortOrder
      ? [{ id: sortByKey, desc: sortOrder === "desc" }]
      : []
  );
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState<number>(pageSize);
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    left: leftPinnedColumnIds,
    right: rightPinnedColumnIds,
  });
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>(initialExpandedRows);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const toggleRow = (rowId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  useEffect(() => {
    if (Object.keys(initialExpandedRows).length > 0) {
      setExpandedRows(initialExpandedRows);
    }
  }, [initialExpandedRows]);

  // --- Add scroll state for horizontal shadow ---
  const [isScrolledLeft, setIsScrolledLeft] = useState(false);
  const [isScrolledRight, setIsScrolledRight] = useState(false);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    function handleScroll() {
      if (!container) return;
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setIsScrolledLeft(scrollLeft > 0);
      setIsScrolledRight(scrollLeft + clientWidth < scrollWidth - 1);
    }

    handleScroll(); // Initial check
    container.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  // Reset page when data changes (more carefully)
  const prevDataRef = useRef<T[]>(data);
  useEffect(() => {
    // Only reset if data has actually changed in a way that implies a fresh set (e.g., search/filter)
    // Check length and first item to be more resilient than just reference check
    if (data !== prevDataRef.current) {
      if (data.length !== prevDataRef.current.length || (data.length > 0 && data[0] !== prevDataRef.current[0])) {
        setCurrentPage(0);
      }
      prevDataRef.current = data;
    }
  }, [data]);

  // Only reset sorting when data or default sort changes (not on every click)
  useEffect(() => {
    if (sortByKey && sortOrder) {
      setSorting([{ id: sortByKey, desc: sortOrder === "desc" }]);
    }
  }, [data, sortByKey, sortOrder]);

  // Initialize and sync currentPageSize with pageSize prop
  useEffect(() => {
    setCurrentPageSize(pageSize);
  }, [pageSize]);

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }

    // Skip the first run to avoid redundant page change calls
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (propOnPageChange && manualPagination) {
      propOnPageChange(currentPage + 1);
    }
  }, [currentPage, manualPagination, propOnPageChange]);

  const filteredData = useMemo(() => {
    if (!search) return data;
    return data.filter((row: T) => {
      // Convert row to a string representation of its values for searching
      const searchableString = Object.entries(row as object)
        .filter(([, value]) => value !== null && value !== undefined)
        .map(([, value]) => {
          if (typeof value === "object") {
            return JSON.stringify(value);
          }
          return String(value);
        })
        .join(" ")
        .toLowerCase();

      return searchableString.includes(search.toLowerCase());
    });
  }, [data, search]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      sorting,
      globalFilter: search,
      pagination: {
        pageIndex: currentPage,
        pageSize: currentPageSize,
      },
      columnPinning,
    },
    onColumnPinningChange: setColumnPinning,
    onSortingChange: (updaterOrValue) => {
      const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
      setSorting(newSorting);
      onSort?.(newSorting);
    },
    pageCount: manualPagination ? propPageCount : Math.ceil(filteredData.length / currentPageSize),
    manualPagination,
    manualSorting,
    manualFiltering,
  });

  const handleSearch = (value: string) => {
    setSearch(value);
    setCurrentPage(0); // Reset to first page when searching
    onSearch?.(value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page - 1);
    if (propOnPageChange) propOnPageChange(page);
  };

  const handlePageSizeChange = (newSize: number) => {
    setCurrentPageSize(newSize);
    setCurrentPage(0); // Reset to first page when changing page size
    if (propOnPageSizeChange) propOnPageSizeChange(newSize);
  };

  // Enhanced summary row calculation
  const summaryRow = useMemo(() => {
    if (!summaryKeys || summaryKeys.length === 0) return null;
    const summary: { [key: string]: React.ReactNode } = {};
    for (const config of summaryKeys) {
      // Check for custom calculation function first
      if (config.customCalculation) {
        const result = config.customCalculation(data);
        summary[config.key] = result;
        continue;
      }

      if (config.customValue !== undefined) {
        summary[config.key] = config.customValue;
        continue;
      }
      let sum = 0;
      if (Array.isArray(config.value)) {
        sum = data.reduce((acc: number, row: T) => {
          let rowSum = 0;
          for (const k of config.value as string[]) {
            const value = getNestedValue(row, k);
            const num =
              typeof value === "string"
                ? Number((value as string).replace(/,/g, ""))
                : Number(value);
            rowSum += isNaN(num) ? 0 : num;
          }
          return acc + rowSum;
        }, 0);
      } else if (typeof config.value === "string") {
        sum = data.reduce((acc: number, row: T) => {
          const value = getNestedValue(row, config.value as string);
          const num =
            typeof value === "string"
              ? Number((value as string).replace(/,/g, ""))
              : Number(value);
          return acc + (isNaN(num) ? 0 : num);
        }, 0);
      } else {
        // fallback: sum the key itself
        sum = data.reduce((acc: number, row: T) => {
          const value = getNestedValue(row, config.key);
          const num =
            typeof value === "string"
              ? Number((value as string).replace(/,/g, ""))
              : Number(value);
          return acc + (isNaN(num) ? 0 : num);
        }, 0);
      }
      summary[config.key] = isNaN(sum)
        ? ""
        : `${config.prepend || ""}${sum.toLocaleString(undefined, {
          minimumFractionDigits: config.precision ?? 2,
          maximumFractionDigits: config.precision ?? 2,
        })}${config.append || ""}`;
    }
    return summary;
  }, [data, summaryKeys]);

  // Render summary row as a <tr>
  const renderSummaryRow = () => {
    if (!summaryRow || !summaryKeys) return null;
    const stickyClass =
      summaryPosition === "top"
        ? "sticky-summary-row-top"
        : summaryPosition === "bottom"
          ? "sticky-summary-row-bottom"
          : "";
    return (
      <tr
        className={`bg-white font-semibold text-gray-700 group ${stickyClass}`}
      >
        {table.getAllLeafColumns().map((column, idx) => {
          const colId = column.id;
          // Find the summary config for this column
          const config = colId
            ? summaryKeys.find((sk) => sk.key === colId)
            : undefined;
          // Use align if provided, fallback to default
          const cellAlign = config?.align || (idx === 0 ? "left" : "right");
          return (
            <td
              key={colId || idx}
              className={cn(
                "px-4 py-3 m-0 bg-white group-hover:bg-gray-50",
                cellAlign === "left" ? "text-left" : cellAlign === "center" ? "text-center" : "text-right"
              )}
              style={{
                ...getCommonPinningStyles(
                  column,
                  isScrolledLeft,
                  isScrolledRight,
                  "both"
                ),
                minWidth: column.columnDef.minSize,
                maxWidth: column.columnDef.maxSize,
                ...(cellAlign === "left" ||
                  cellAlign === "right" ||
                  cellAlign === "center"
                  ? { textAlign: cellAlign as "left" | "right" | "center" }
                  : {}),
              }}
            >
              <span className={cn(
                (!colId || !summaryRow[colId]) && "opacity-0",
                "text-xs uppercase", // Default styling
                config?.className
              )}>
                {colId && summaryRow[colId] ? summaryRow[colId] : "-"}
              </span>
            </td>
          );
        })}
      </tr>
    );
  };

  // Custom header click handler for sorting
  function handleHeaderClick(columnId: string) {
    const newSorting: SortingState =
      sorting.length === 0 || sorting[0].id !== columnId
        ? [{ id: columnId, desc: true }]
        : [{ id: columnId, desc: !sorting[0].desc }];
    setSorting(newSorting);
    onSort?.(newSorting);
  }

  const totalPages = manualPagination ? (propPageCount || 0) : Math.ceil(filteredData.length / currentPageSize);

  // Remove handlePageSizeChange as it's defined above now

  return (
    <div className={`space-y-6 ${className}`}>
      {showSearch && (
        <div className="flex items-center justify-between mb-6">
          {title || <div />}
          <div className={`flex items-center justify-${searchPosition} gap-2`}>
            {prependWithSearch}
            <input
              type="text"
              placeholder={t("common.search")}
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="max-w-sm px-4 py-2 border border-gray-300 rounded-lg focus:ring-gray-300 focus:border-gray-300"
            />
            {appendWithSearch}
          </div>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <div
            id="table-container"
            ref={tableContainerRef}
            className="overflow-y-auto"
            style={{ maxHeight }}
          >
            <div style={{ width: "100%" }}>
              <table className="w-full text-sm text-left text-gray-500 table-auto border-collapse">
                <thead className="text-xs text-gray-700 uppercase bg-white sticky top-0 z-10">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className="bg-white group">
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className={`py-3 px-4 bg-white group-hover:bg-gray-50  ${header.column.getCanSort()
                            ? "cursor-pointer select-none"
                            : ""
                            }`}
                          onClick={() => header.column.getCanSort() && handleHeaderClick(header.column.id)}
                          style={{
                            ...getCommonPinningStyles(
                              header.column,
                              isScrolledLeft,
                              isScrolledRight
                            ),
                            minWidth: header.column.columnDef.minSize,
                            maxWidth: header.column.columnDef.maxSize,
                            textAlign:
                              header.column.columnDef.meta?.headerAlign ||
                              "left",
                          }}
                        >
                          <div
                            className={`flex items-center gap-2 ${header.column.columnDef.meta?.headerAlign ===
                              "right"
                              ? "justify-end"
                              : header.column.columnDef.meta?.headerAlign ===
                                "center"
                                ? "justify-center"
                                : "justify-start"
                              }`}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            {{
                              asc: " ↑",
                              desc: " ↓",
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                  {summaryPosition === "top" && renderSummaryRow()}
                </thead>
                <tbody>
                  {isLoading ? (
                    Array.from({ length: loadingRowCount }).map((_, index) => (
                      <tr key={index}>
                        {columns.map((_, colIndex) => (
                          <td
                            key={colIndex}
                            className="px-4 py-3"
                            style={{ height: `${loadingHeight}rem` }}
                          >
                            <div className="animate-pulse bg-gray-200 h-full rounded"></div>
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : table.getRowModel().rows.length > 0 ? (
                    table.getRowModel().rows.map((row) => (
                      <Fragment key={row.id}>
                        <tr
                          className={`bg-white hover:bg-gray-50 group ${onRowClick || renderRowDetails ? "cursor-pointer" : ""
                            }`}
                          onClick={() => {
                            if (onRowClick) onRowClick(row.original);
                            if (renderRowDetails) toggleRow(row.id);
                          }}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              className="px-4 py-3 bg-white group-hover:bg-gray-50"
                              style={{
                                ...getCommonPinningStyles(
                                  cell.column,
                                  isScrolledLeft,
                                  isScrolledRight
                                ),
                                minWidth: cell.column.columnDef.minSize,
                                maxWidth: cell.column.columnDef.maxSize,
                                textAlign: cell.column.columnDef.meta?.headerAlign || "left",
                              }}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                {
                                  ...cell.getContext(),
                                  isExpanded: expandedRows[row.id]
                                }
                              )}
                            </td>
                          ))}
                        </tr>
                        <AnimatePresence initial={false}>
                          {expandedRows[row.id] && renderRowDetails && (
                            <motion.tr
                              key={`expanded-${row.id}`}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              className="bg-gray-50/50"
                            >
                              <td colSpan={columns.length} className="p-0 border-b">
                                <motion.div
                                  initial={{ height: 0 }}
                                  animate={{ height: "auto" }}
                                  exit={{ height: 0 }}
                                  transition={{ duration: 0.2, ease: "easeInOut" }}
                                  className="overflow-hidden"
                                >
                                  {renderRowDetails(row.original)}
                                </motion.div>
                              </td>
                            </motion.tr>
                          )}
                        </AnimatePresence>
                      </Fragment>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        {t("common.noData")}
                      </td>
                    </tr>
                  )}
                </tbody>
                {summaryPosition === "bottom" && (
                  <tfoot>{renderSummaryRow()}</tfoot>
                )}
              </table>
            </div>
          </div>
        </div>

        {showPagination && !isLoading && filteredData.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 bg-white">
            <Pagination
              currentPage={currentPage + 1}
              totalPages={totalPages}
              totalItems={manualPagination ? (totalItemsCount || 0) : filteredData.length}
              itemsPerPage={currentPageSize}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handlePageSizeChange}
              showItemsPerPage={true}
              loading={isLoading}
            />
          </div>
        )}
      </div>
    </div>
  );
}
